// Delivery city picker (owner, 2026-10-25: «مطوية؛ مع خيار انه يكتب مدينته،
// اختياري، يظهر جميع اسماء المدن، وفيه خيار بحث»). One closed field; opening
// it shows a search box over every city in KSA_CITIES, a «use what I typed»
// row when the city is not listed. REQUIRED on the request forms (owner,
// 2026-10-25: «يجب يكون اجباري» — reversing the same day's «اختياري»);
// the factory quote too since the same evening («الزامية» on every form).
// required={false} (no caller today) adds a «no city» row that clears it. The word «اختياري» is never shown (owner,
// 2026-10-25: «احذف اختياري»).
// Mirror of the app's components/CityPicker.js.
//
// What is stored: a listed city stores its ENGLISH name, as before — it
// travels to a Chinese factory. A typed city is stored as typed, unless it
// folds to a listed name, which then stores that name instead.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { KSA_CITIES, searchCities, foldCityName } from '../lib/requestFormOptions';

const T = {
  ar: { placeholder: 'اختر مدينتك', placeholderOpt: 'اختر مدينتك', search: 'ابحث عن مدينتك', use: (x) => `استخدم «${x}»`, none: 'بدون مدينة', empty: 'ما لقينا مدينة بهذا الاسم — اكتبها واستخدمها كما هي.' },
  en: { placeholder: 'Choose your city', placeholderOpt: 'Choose your city', search: 'Search for your city', use: (x) => `Use “${x}”`, none: 'No city', empty: 'No city by that name — type it and use it as it is.' },
  zh: { placeholder: '选择城市', placeholderOpt: '选择城市', search: '搜索城市', use: (x) => `使用“${x}”`, none: '不选城市', empty: '没有找到该城市——输入后可直接使用。' },
};

/** The label for a stored value: a listed city in the viewer's language, anything else as typed. */
export function cityDisplay(value, lang) {
  const v = String(value || '').trim();
  if (!v) return '';
  const hit = KSA_CITIES.find((c) => c.en === v || c.ar === v);
  return hit ? (lang === 'ar' ? hit.ar : hit.en) : v;
}

export default function CityPicker({ value, onChange, lang = 'ar', className = 'form-input', style, required = true }) {
  const isAr = lang === 'ar';
  const t = T[lang] || T.ar;
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef(null);
  const list = useMemo(() => searchCities(q), [q]);
  const typed = q.trim();
  const exact = typed && KSA_CITIES.find((c) => foldCityName(c.ar) === foldCityName(typed) || foldCityName(c.en) === foldCityName(typed));

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const pick = (v) => { onChange(v); setOpen(false); setQ(''); };
  const shown = cityDisplay(value, lang);
  const row = { display: 'block', width: '100%', textAlign: isAr ? 'right' : 'left', background: 'none', border: 'none', borderBottom: '1px solid var(--border)', padding: '11px 14px', fontSize: 14, cursor: 'pointer', fontFamily: font, color: 'var(--text-primary)' };

  return (
    <div ref={box} style={{ position: 'relative', ...style }} dir={isAr ? 'rtl' : 'ltr'}>
      <button type="button" className={className} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, cursor: 'pointer', textAlign: isAr ? 'right' : 'left', fontFamily: font, color: shown ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shown || (required ? t.placeholder : t.placeholderOpt)}</span>
        <span aria-hidden="true" style={{ color: 'var(--text-secondary)' }}>▾</span>
      </button>
      {open && (
        <div role="dialog" style={{ position: 'absolute', insetInline: 0, top: 'calc(100% + 6px)', zIndex: 80, background: 'var(--surface-raised, #fff)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 12px 32px rgba(26,24,20,0.14)', overflow: 'hidden' }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.search} aria-label={t.search}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter') { e.preventDefault(); if (typed) pick(exact ? exact.en : typed); } }}
            style={{ width: '100%', boxSizing: 'border-box', border: 'none', borderBottom: '1px solid var(--border)', padding: '12px 14px', fontSize: 14, outline: 'none', fontFamily: font, background: 'var(--bg-page)' }} />
          <div role="listbox" style={{ maxHeight: 280, overflowY: 'auto' }}>
            {!required && !!value && !typed && <button type="button" style={{ ...row, color: 'var(--text-secondary)' }} onClick={() => pick('')}>{t.none}</button>}
            {list.map((c) => (
              <button type="button" role="option" aria-selected={value === c.en} key={c.en} style={{ ...row, fontWeight: value === c.en ? 700 : 400 }} onClick={() => pick(c.en)}>
                {isAr ? c.ar : c.en}{value === c.en ? ' ✓' : ''}
              </button>
            ))}
            {/* «Use what I typed» after the matches, so a half-typed word is
                never the first row; with no match it follows the note. */}
            {!list.length && <p style={{ margin: 0, padding: '12px 14px', fontSize: 13, color: 'var(--text-secondary)', fontFamily: font, lineHeight: 1.8 }}>{t.empty}</p>}
            {!!typed && !exact && <button type="button" style={{ ...row, color: 'var(--accent, #6E5A3F)', fontWeight: 700 }} onClick={() => pick(typed)}>{t.use(typed)}</button>}
          </div>
        </div>
      )}
    </div>
  );
}
